import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DelegationCreateEditModal from './DelegationCreateEditModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import { delegationsService } from '../../../../services/delegationsService';
import { getUsersPaged } from '../../../../services/usersServices';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { delegationsI18n } from './i18n';

const normalizeText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export default function Delegations() {
  const { language } = useLanguage();
  const delegationsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: delegationsI18n });
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const {
    Card,
    Button,
    Table,
    Badge,
    IconButton,
    Modal,
    Input,
    Dropdown,
    ComboBox,
    Checkbox,
  } = DesignSystemComponents ?? {};

  const { canCreateSubstitution, canEditSubstitution, canDeleteSubstitution } =
    usePermissionsConfiguration();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedDelegation, setSelectedDelegation] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [users, setUsers] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [hasCatalogReasons, setHasCatalogReasons] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [catalogStatusLabels, setCatalogStatusLabels] = useState({});
  const [defaultStatusId, setDefaultStatusId] = useState(null);

  useEffect(() => {
    delegationsI18n.changeLanguage(delegationsLanguage);
  }, [delegationsLanguage]);

  const baseStatusLabels = useMemo(
    () => ({
      1: t('delegations_status_scheduled'),
      2: t('delegations_status_active'),
      3: t('delegations_status_finished'),
    }),
    [t]
  );

  const defaultReasonOptions = useMemo(
    () => [
      { label: t('delegations_reason_vacation'), value: 'Vacaciones' },
      {
        label: t('delegations_reason_medical_leave'),
        value: 'Licencia medica',
      },
      { label: t('delegations_reason_leave'), value: 'Permiso' },
      { label: t('delegations_reason_other'), value: 'Otro' },
    ],
    [t]
  );

  useEffect(() => {
    if (!hasCatalogReasons) {
      setReasonOptions(defaultReasonOptions);
    }
  }, [defaultReasonOptions, hasCatalogReasons]);

  const formatDateDisplay = (value) => {
    if (!value) return '';
    const datePart = String(value).slice(0, 10);
    const parts = datePart.split('-');
    if (parts.length !== 3) return datePart;
    const [yyyy, mm, dd] = parts;
    return `${dd}-${mm}-${yyyy}`;
  };

  const statusLabel = (statusValue) => {
    if (
      statusValue === null ||
      statusValue === undefined ||
      statusValue === ''
    ) {
      return t('delegations_status_generic', { value: '-' });
    }

    const fromCatalog = catalogStatusLabels?.[statusValue];
    if (fromCatalog) return fromCatalog;

    const numeric = Number(statusValue);
    if (!Number.isNaN(numeric)) {
      return (
        catalogStatusLabels?.[numeric] ||
        baseStatusLabels?.[numeric] ||
        t('delegations_status_generic', { value: numeric })
      );
    }

    return String(statusValue);
  };

  const normalizeStatusCode = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) return numeric;
    return null;
  };

  const statusColor = (row) => {
    const code = normalizeStatusCode(
      row?.statusCode ?? row?.estado ?? row?.statusId
    );
    if (code === 2) return 'success';
    if (code === 1) return 'info';
    if (code === 3) return 'light';

    const statusText = normalizeText(row?.statusName ?? row?.status);
    if (
      ['activa', 'active', 'ativa'].includes(statusText) ||
      statusText.includes('activ')
    ) {
      return 'success';
    }
    if (
      ['programada', 'scheduled', 'programada'].includes(statusText) ||
      statusText.includes('program')
    ) {
      return 'info';
    }

    return 'light';
  };

  const mapDelegation = (item) => {
    const start = item?.startDate ?? item?.fechaInicio;
    const end = item?.endDate ?? item?.fechaFin;
    const statusName =
      item?.statusName ?? item?.estadoNombre ?? item?.estadoDescripcion ?? '';
    const statusCode =
      item?.statusId ?? item?.estadoId ?? item?.estado ?? item?.status ?? null;

    return {
      id: item?.id,
      fromUser:
        item?.outgoingUserName ??
        item?.usuarioTitularNombre ??
        item?.usuarioTitular?.nombre ??
        item?.usuarioTitular?.name ??
        String(
          item?.outgoingUserId ??
            item?.usuarioTitularId ??
            item?.usuarioTitular?.id ??
            ''
        ),
      toUser:
        item?.substituteUserName ??
        item?.usuarioSuplenteNombre ??
        item?.usuarioSuplente?.nombre ??
        item?.usuarioSuplente?.name ??
        String(
          item?.substituteUserId ??
            item?.usuarioSuplenteId ??
            item?.usuarioSuplente?.id ??
            ''
        ),
      fromUserId:
        item?.outgoingUserId ??
        item?.usuarioTitularId ??
        item?.usuarioTitular?.id,
      toUserId:
        item?.substituteUserId ??
        item?.usuarioSuplenteId ??
        item?.usuarioSuplente?.id,
      startDate: start ? String(start).slice(0, 10) : '',
      endDate: end ? String(end).slice(0, 10) : '',
      statusName,
      statusCode,
      status: statusName || statusLabel(statusCode),
      estado: item?.estado ?? item?.status,
      motivo: item?.motivo ?? item?.reason ?? item?.reasonText ?? '',
      typeId: item?.typeId ?? item?.tipoId ?? null,
      typeName: item?.typeName ?? item?.tipoNombre ?? '',
      statusId: statusCode,
    };
  };

  const extractList = (response) => {
    const data = response?.data ?? response;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.results)) return response.results;
    return [];
  };

  const extractTotal = (response, list) => {
    const data = response?.data ?? response;
    return (
      data?.totalCount ??
      data?.total ??
      data?.totalItems ??
      data?.count ??
      response?.totalCount ??
      response?.total ??
      response?.totalItems ??
      response?.count ??
      list.length
    );
  };

  const fetchDelegations = async () => {
    try {
      setLoading(true);
      const response = await delegationsService.getPaged({
        page: page + 1,
        pageSize,
      });
      const list = extractList(response);
      const total = extractTotal(response, list);
      const mapped = list.map(mapDelegation);
      setRows(mapped);
      setTotalItems(total);
    } catch (error) {
      console.error('Error loading delegations:', error);
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const result = await getUsersPaged(1, 1500, true);
      setUsers(result.users ?? []);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const response = await delegationsService.getCatalogs();
      const catalogs = response?.data ?? response;

      const rawReasons =
        catalogs?.motivos ??
        catalogs?.motivosSuplencia ??
        catalogs?.motivosSuplencias ??
        catalogs?.reasons ??
        catalogs?.reasonOptions ??
        [];

      const normalizeOptions = (items) =>
        (items || [])
          .map((item) => {
            if (item === null || item === undefined) return null;
            if (typeof item === 'string') {
              return { label: item, value: item };
            }
            const label =
              item.label ??
              item.name ??
              item.descripcion ??
              item.description ??
              item.valor ??
              item.value ??
              item.id;
            const value =
              item.value ?? item.id ?? item.code ?? item.name ?? item.label;

            if (label === null || label === undefined) return null;
            if (value === null || value === undefined) return null;

            return { label: String(label), value };
          })
          .filter(Boolean);

      const reasonList = normalizeOptions(rawReasons);
      if (reasonList.length) {
        setReasonOptions(reasonList);
        setHasCatalogReasons(true);
      }

      const rawStatuses =
        catalogs?.estados ?? catalogs?.status ?? catalogs?.statuses ?? [];
      const statusList = normalizeOptions(rawStatuses);

      if (statusList.length) {
        setStatusOptions(statusList);
        const statusMap = {};
        statusList.forEach((statusItem) => {
          if (statusItem.value !== null && statusItem.value !== undefined) {
            statusMap[statusItem.value] = statusItem.label;
            const numeric = Number(statusItem.value);
            if (!Number.isNaN(numeric)) {
              statusMap[numeric] = statusItem.label;
            }
          }
        });
        setCatalogStatusLabels(statusMap);
      }

      const defaultStatus = (rawStatuses || []).find(
        (item) => item?.default || item?.isDefault || item?.porDefecto
      );
      if (defaultStatus) {
        const defaultId =
          defaultStatus.value ?? defaultStatus.id ?? defaultStatus.code ?? null;
        if (defaultId !== null && defaultId !== undefined) {
          setDefaultStatusId(defaultId);
        }
      }
    } catch (error) {
      console.error('Error loading catalogs:', error);
    }
  };

  useEffect(() => {
    fetchDelegations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    fetchUsers();
    fetchCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  const onCreate = () => {
    setModalMode('create');
    setSelectedDelegation(null);
    setShowModal(true);
  };

  const onEdit = async (row) => {
    setModalMode('edit');
    setSelectedDelegation(row);
    setShowModal(true);

    const mergeFilled = (base, next) => {
      if (!next) return base;
      const cleaned = Object.fromEntries(
        Object.entries(next).filter(
          ([, value]) => value !== '' && value !== null && value !== undefined
        )
      );
      return { ...base, ...cleaned };
    };

    try {
      const detailResponse = await delegationsService.getById(row.id);
      const detailData = detailResponse?.data ?? detailResponse;
      const detail = detailData?.data ?? detailData;
      const normalized = mapDelegation(detail);
      setSelectedDelegation((prev) => mergeFilled(prev ?? row, normalized));
    } catch (error) {
      console.error('Error loading delegation detail:', error);
    }
  };

  const onDelete = (row) => {
    if (!row?.id) return;
    setDeleteTarget(row);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await delegationsService.delete(deleteTarget.id);
      await fetchDelegations();
    } catch (error) {
      console.error('Error deleting delegation:', error);
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const handleCloseDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        label: user.name ?? user.email ?? String(user.id),
        value: user.id,
      })),
    [users]
  );

  if (!Card || !Button || !Table || !Badge || !Modal || !Input || !Dropdown)
    return null;

  const handleSaveDelegation = async (payload) => {
    try {
      if (modalMode === 'create') {
        await delegationsService.create(payload);
      } else {
        await delegationsService.update(payload.id, payload);
      }
      setShowModal(false);
      await fetchDelegations();
    } catch (error) {
      console.error('Error saving delegation:', error);
    }
  };

  return (
    <>
      <Card specialPadding='p-4'>
        <div className='d-flex justify-content-between mb-3'>
          <div>
            <h2 className='mb-1'>{t('delegations_title')}</h2>
            <p className='caption font-light'>{t('delegations_subtitle')}</p>
          </div>
          <Button
            label={t('delegations_new_button')}
            size='sm'
            variant='primary'
            iconStart='plusLg'
            onClick={onCreate}
            disabled={!canCreateSubstitution}
          />
        </div>
        <div className='table-container'>
          <Table
            columns={[
              { accessor: 'fromUser', label: t('delegations_col_from_user') },
              { accessor: 'toUser', label: t('delegations_col_to_user') },
              {
                accessor: 'typeName',
                label: t('delegations_col_type'),
                render: (row) => {
                  const label = row.typeName || (row.typeId === 2 ? t('delegations_modal_type_permanent') : t('delegations_modal_type_temporary'));
                  return (
                    <span style={row.typeId === 2 ? { color: 'var(--error-600)', fontWeight: 600 } : undefined}>
                      {label}
                    </span>
                  );
                },
              },
              {
                accessor: 'startDate',
                label: t('delegations_col_start_date'),
                render: (row) => formatDateDisplay(row.startDate),
              },
              {
                accessor: 'endDate',
                label: t('delegations_col_end_date'),
                render: (row) =>
                  row.typeId === 2 ? '-' : formatDateDisplay(row.endDate),
              },
              {
                accessor: 'status',
                label: t('delegations_col_status'),
                render: (row) => (
                  <Badge
                    text={
                      row.statusName ||
                      statusLabel(row.statusCode ?? row.status)
                    }
                    color={statusColor(row)}
                    size='sm'
                  />
                ),
              },
              {
                label: t('delegations_col_actions'),
                render: (row) => {
                  const isPermanent = row.typeId === 2;
                  const isFinished =
                    normalizeStatusCode(row.statusCode ?? row.statusId) === 3;
                  const showEdit = !isPermanent && !isFinished && canEditSubstitution;
                  const showDelete =
                    !isPermanent && !isFinished && canDeleteSubstitution;

                  return (
                    <div className='d-flex gap-3'>
                      {showEdit && (
                        <IconButton
                          icon='pencil'
                          onClick={() => onEdit(row)}
                          size='md'
                          ariaLabel={t('delegations_action_edit')}
                          iconStroke='var(--primary-500)'
                        />
                      )}
                      {showDelete && (
                        <IconButton
                          icon='trash'
                          onClick={() => onDelete(row)}
                          size='md'
                          ariaLabel={t('delegations_action_delete')}
                          iconStroke='var(--error-500)'
                        />
                      )}
                    </div>
                  );
                },
              },
            ]}
            data={rows}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            pageSizes={[10, 25, 50]}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            onPageSizeChange={handlePageSizeChange}
            totalItems={totalItems}
            paginator
            loading={loading}
          />
        </div>
      </Card>
      <DelegationCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        ComboBox={ComboBox}
        Checkbox={Checkbox}
        show={showModal}
        mode={modalMode}
        initialData={selectedDelegation}
        userOptions={userOptions}
        reasonOptions={reasonOptions}
        statusOptions={statusOptions}
        defaultStatusId={defaultStatusId}
        onClose={() => setShowModal(false)}
        onSave={handleSaveDelegation}
      />
      <ConfirmModal
        Modal={Modal}
        show={showDeleteConfirm}
        title={t('delegations_confirm_delete_title')}
        message={(() => {
          if (!deleteTarget) return t('delegations_confirm_delete_fallback');
          const isPermanent = deleteTarget.typeId === 2;
          const statusCode = normalizeStatusCode(
            deleteTarget.statusCode ?? deleteTarget.statusId
          );
          if (isPermanent) {
            return t('delegations_confirm_delete_permanent', {
              fromUser: deleteTarget.fromUser,
            });
          }
          if (statusCode === 1) {
            return t('delegations_confirm_delete_scheduled', {
              fromUser: deleteTarget.fromUser,
              toUser: deleteTarget.toUser,
            });
          }
          if (statusCode === 2) {
            return t('delegations_confirm_delete_active', {
              fromUser: deleteTarget.fromUser,
              toUser: deleteTarget.toUser,
            });
          }
          return t('delegations_confirm_delete_message', {
            fromUser: deleteTarget.fromUser,
            toUser: deleteTarget.toUser,
          });
        })()}
        confirmLabel={t('delegations_confirm_delete_button')}
        cancelLabel={t('delegations_cancel')}
        confirmVariant='danger'
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDelete}
      />
    </>
  );
}
